import { useCallback, useMemo, useState } from 'react';

/** One record's staged edits. Structurally identical to the tablemaint bulk payload
 *  and to a change-all grid item — this module stays agnostic about which. */
export interface StagedChange {
  recno: number | string;
  values: Record<string, unknown>;
}

/**
 * Grid edits held until the user saves them.
 *
 * Nothing typed into a cell or dragged across a range reaches the server on its own
 * — it lands here, the cell shows it as pending, and one Save sends the lot in a
 * single request. That is what makes a mis-drag recoverable: Discard puts everything
 * back, because nothing was written.
 *
 * Keyed by record id, then column. A record that fails to save STAYS staged (the
 * caller passes the failures to `keepOnly`) so the user can fix it and retry rather
 * than losing the edit along with the ones that succeeded.
 */

export type StagedEdits = Record<string, Record<string, unknown>>;

export interface StagedEditsApi {
  edits: StagedEdits;
  /** How many RECORDS carry an edit — what the Save button counts. */
  recordCount: number;
  /** How many cells were changed, across all records. */
  cellCount: number;
  /** The value in force for a cell: the staged edit if any, else the stored value. */
  effective: (recno: unknown, column: string, stored: unknown) => unknown;
  isEdited: (recno: unknown, column: string) => boolean;
  /** Stage one cell. Pass `original` (the STORED value) and a commit that matches it
   *  un-stages instead: typing into a field and clearing it again has to leave no
   *  trace, or the row stays highlighted and the counts keep counting a no-op. */
  stage: (recno: unknown, column: string, value: unknown, original: unknown) => void;
  /** Stage one value across many records — a drag-select. Each entry carries its own
   *  stored value, so rows that already hold the value are not staged at all. */
  stageMany: (
    entries: { recno: unknown; original: unknown }[],
    column: string,
    value: unknown,
  ) => void;
  discard: () => void;
  /** Drop everything except these record ids — used to keep failures staged. */
  keepOnly: (recnos: (number | string)[]) => void;
  /** Drop the staged cells a predicate selects, leaving the rest. Used to end an
   *  edit session scoped to one column: turning editing off for it should undo what it
   *  staged, not leave changes pending behind a switch that says editing is off. */
  discardCells: (matches: (key: string, column: string) => boolean) => void;
  /** The request payload. */
  changes: StagedChange[];
}

const idOf = (recno: unknown): string => String(recno ?? '');

/**
 * Is this commit the value the record already holds?
 *
 * Blank-ish values all collapse together (null, undefined, '' and whitespace), which
 * is the case that matters most: a field that was empty, typed into, then cleared has
 * to come back to "unchanged". Numbers compare numerically so '3' does not read as a
 * change from the stored '3.00'.
 */
export function isUnchanged(value: unknown, original: unknown): boolean {
  const a = value == null ? '' : String(value).trim();
  const b = original == null ? '' : String(original).trim();
  if (a === b) return true;
  if (a === '' || b === '') return false;
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

/** Drop one column from one record, and the record too once nothing is left. */
const withoutCell = (edits: StagedEdits, id: string, column: string): StagedEdits => {
  const values = edits[id];
  if (!values || !(column in values)) return edits;
  const { [column]: _dropped, ...rest } = values;
  const next = { ...edits };
  if (Object.keys(rest).length) next[id] = rest;
  else delete next[id];
  return next;
};

export function useStagedEdits(): StagedEditsApi {
  const [edits, setEdits] = useState<StagedEdits>({});

  const stage = useCallback(
    (recno: unknown, column: string, value: unknown, original: unknown) => {
      const id = idOf(recno);
      if (!id) return;
      setEdits((prev) =>
        isUnchanged(value, original)
          ? withoutCell(prev, id, column)
          : { ...prev, [id]: { ...prev[id], [column]: value } });
    },
    [],
  );

  const stageMany = useCallback(
    (entries: { recno: unknown; original: unknown }[], column: string, value: unknown) => {
      setEdits((prev) => {
        let next = prev;
        for (const entry of entries) {
          const id = idOf(entry.recno);
          if (!id) continue;
          // A drag across rows that already hold the value stages nothing for them,
          // so the count reflects what would actually be written.
          if (isUnchanged(value, entry.original)) {
            next = withoutCell(next, id, column);
            continue;
          }
          next = { ...next, [id]: { ...next[id], [column]: value } };
        }
        return next;
      });
    },
    [],
  );

  const discard = useCallback(() => setEdits({}), []);

  const discardCells = useCallback((matches: (key: string, column: string) => boolean) => {
    setEdits((prev) => {
      const next: StagedEdits = {};
      let changed = false;
      for (const [key, values] of Object.entries(prev)) {
        const kept = Object.fromEntries(
          Object.entries(values).filter(([column]) => !matches(key, column)),
        );
        if (Object.keys(kept).length !== Object.keys(values).length) changed = true;
        // A record with nothing left staged drops out entirely, so the record count
        // and the row highlight settle with it.
        if (Object.keys(kept).length) next[key] = kept;
      }
      return changed ? next : prev;
    });
  }, []);

  const keepOnly = useCallback((recnos: (number | string)[]) => {
    const keep = new Set(recnos.map(idOf));
    setEdits((prev) => Object.fromEntries(
      Object.entries(prev).filter(([id]) => keep.has(id)),
    ));
  }, []);

  const effective = useCallback((recno: unknown, column: string, stored: unknown) => {
    const staged = edits[idOf(recno)];
    return staged && column in staged ? staged[column] : stored;
  }, [edits]);

  const isEdited = useCallback(
    (recno: unknown, column: string) => {
      const staged = edits[idOf(recno)];
      return !!staged && column in staged;
    },
    [edits],
  );

  const changes = useMemo<StagedChange[]>(
    () => Object.entries(edits).map(([recno, values]) => ({ recno, values })),
    [edits],
  );

  const cellCount = useMemo(
    () => Object.values(edits).reduce((n, v) => n + Object.keys(v).length, 0),
    [edits],
  );

  return {
    edits,
    recordCount: Object.keys(edits).length,
    cellCount,
    effective,
    isEdited,
    stage,
    stageMany,
    discard,
    keepOnly,
    discardCells,
    changes,
  };
}

export default useStagedEdits;
